import { useMemo, useState } from 'react';
import { loadState, saveState, formatUSD } from '../lib/storage';
import * as Icons from 'lucide-react';
import { Save, RefreshCcw, PiggyBank, Target } from 'lucide-react';
import { toast } from 'sonner';

const iconFor = (name) => {
  const map = {
    'user-round': Icons.UserRound,
    'shield': Icons.Shield,
    'zap': Icons.Zap,
    'trash-2': Icons.Trash2,
    'droplets': Icons.Droplets,
    'printer': Icons.Printer,
    'trees': Icons.Trees,
    'utensils': Icons.Utensils,
    'book-open': Icons.BookOpen,
    'calendar-heart': Icons.CalendarHeart,
    'notebook-pen': Icons.NotebookPen,
    'spray-can': Icons.SprayCan,
    'boxes': Icons.Boxes,
    'hammer': Icons.Hammer,
  };
  return map[name] || Icons.Circle;
};

export default function Categories() {
  const initial = useMemo(() => loadState(), []);
  const [cats, setCats] = useState(initial.categories);
  const [monthlyBudget, setMonthlyBudget] = useState(initial.monthlyBudget);

  const totalBudget = cats.reduce((a, c) => a + Number(c.budget || 0), 0);
  const totalSpent = cats.reduce((a, c) => a + Number(c.spent || 0), 0);
  const remaining = Math.max(0, monthlyBudget - totalSpent);
  const overCap = totalBudget > monthlyBudget;

  const updateField = (id, field, val) => {
    setCats((prev) => prev.map((c) => (c.id === id ? { ...c, [field]: Number(val) || 0 } : c)));
  };

  const saveAll = () => {
    const next = { ...initial, categories: cats, monthlyBudget: Number(monthlyBudget) || 0 };
    saveState(next);
    toast.success('Categories saved', { description: 'Your budgets have been updated.' });
  };

  const resetOne = (id) => {
    const original = initial.categories.find((c) => c.id === id);
    if (!original) return;
    setCats((prev) => prev.map((c) => (c.id === id ? original : c)));
  };

  return (
    <div className="space-y-8" data-testid="categories-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-[var(--gcbc-red-deep)] font-bold">Category management</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 mt-1">Update your budgets</h1>
          <p className="text-sm text-slate-600 mt-1">Enter and update every category — from Pastor salary to remodeling projects.</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={saveAll}
            data-testid="categories-save-btn"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[var(--gcbc-royal)] hover:bg-[var(--gcbc-royal-deep)] text-white font-semibold shadow-sm transition-colors"
          >
            <Save className="w-4 h-4" /> Save changes
          </button>
        </div>
      </div>

      {/* Monthly budget + totals */}
      <div className="grid md:grid-cols-3 gap-4">
        <div className="gcbc-accent-top rounded-2xl bg-white border border-slate-200 p-5 shadow-sm" data-testid="monthly-budget-card">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase tracking-widest font-bold text-slate-500">Total monthly budget</div>
            <div className="w-9 h-9 rounded-xl bg-blue-50 text-[var(--gcbc-royal-deep)] flex items-center justify-center">
              <PiggyBank className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-3">
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-semibold">$</span>
              <input
                type="number"
                value={monthlyBudget}
                onChange={(e) => setMonthlyBudget(Number(e.target.value) || 0)}
                data-testid="monthly-budget-input"
                className="w-full pl-7 pr-3 py-2.5 rounded-lg border border-slate-300 bg-white focus:border-[var(--gcbc-royal)] focus:ring-2 focus:ring-blue-100 outline-none font-display text-2xl font-bold text-slate-900"
              />
            </div>
            <div className={`text-xs mt-2 ${overCap ? 'text-[var(--gcbc-red-deep)]' : 'text-slate-500'}`}>
              {overCap
                ? `Category budgets (${formatUSD(totalBudget)}) exceed monthly budget.`
                : `Remaining after category budgets: ${formatUSD(Math.max(0, monthlyBudget - totalBudget))}`}
            </div>
          </div>
        </div>

        <div className="gcbc-accent-top rounded-2xl bg-white border border-slate-200 p-5 shadow-sm" data-testid="category-budget-total">
          <div className="text-xs uppercase tracking-widest font-bold text-slate-500">Category budgets total</div>
          <div className="font-display text-3xl font-black text-[var(--gcbc-royal-deep)] mt-3">{formatUSD(totalBudget)}</div>
          <div className="text-xs text-slate-500 mt-1">{cats.length} categories tracked</div>
        </div>

        <div className="gcbc-accent-top rounded-2xl bg-white border border-slate-200 p-5 shadow-sm" data-testid="category-spent-total">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase tracking-widest font-bold text-slate-500">Spent this month</div>
            <div className="w-9 h-9 rounded-xl bg-red-50 text-[var(--gcbc-red-deep)] flex items-center justify-center">
              <Target className="w-4 h-4" />
            </div>
          </div>
          <div className="font-display text-3xl font-black text-slate-900 mt-3">{formatUSD(totalSpent)}</div>
          <div className="text-xs text-slate-500 mt-1">{formatUSD(remaining)} remaining vs monthly budget</div>
        </div>
      </div>

      {/* Category cards grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="category-cards">
        {cats.map((c, i) => {
          const Icon = iconFor(c.icon);
          const pct = Math.min(100, Math.round((Number(c.spent) / Math.max(1, Number(c.budget))) * 100));
          const over = Number(c.spent) > Number(c.budget);
          return (
            <div
              key={c.id}
              data-testid={`category-card-${c.id}`}
              className="gcbc-accent-top gcbc-rise rounded-2xl bg-white border border-slate-200 p-5 shadow-sm hover:shadow-md transition-shadow"
              style={{ animationDelay: `${i * 40}ms` }}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-blue-50 text-[var(--gcbc-royal-deep)] flex items-center justify-center">
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="font-display font-bold text-slate-900 leading-tight">{c.name}</div>
                    <div className="text-[11px] uppercase tracking-widest text-slate-500 mt-0.5">{pct}% used {over ? '· over' : ''}</div>
                  </div>
                </div>
                <button
                  onClick={() => resetOne(c.id)}
                  data-testid={`category-reset-${c.id}`}
                  className="text-slate-400 hover:text-[var(--gcbc-red-deep)] transition-colors p-1"
                  aria-label="Reset category"
                  type="button"
                >
                  <RefreshCcw className="w-4 h-4" />
                </button>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-2">
                <label className="block">
                  <span className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">Budget</span>
                  <div className="relative mt-1">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                    <input
                      type="number"
                      value={c.budget}
                      onChange={(e) => updateField(c.id, 'budget', e.target.value)}
                      data-testid={`category-budget-input-${c.id}`}
                      className="w-full pl-6 pr-2 py-2 rounded-lg border border-slate-300 bg-white focus:border-[var(--gcbc-royal)] focus:ring-2 focus:ring-blue-100 outline-none text-slate-900 font-semibold"
                    />
                  </div>
                </label>
                <label className="block">
                  <span className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">Spent</span>
                  <div className="relative mt-1">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                    <input
                      type="number"
                      value={c.spent}
                      onChange={(e) => updateField(c.id, 'spent', e.target.value)}
                      data-testid={`category-spent-input-${c.id}`}
                      className="w-full pl-6 pr-2 py-2 rounded-lg border border-slate-300 bg-white focus:border-[var(--gcbc-royal)] focus:ring-2 focus:ring-blue-100 outline-none text-slate-900 font-semibold"
                    />
                  </div>
                </label>
              </div>

              <div className="mt-4">
                <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{
                      width: `${pct}%`,
                      background: over ? 'linear-gradient(90deg,#ef4444,#7f1d1d)' : 'linear-gradient(90deg,#3b82f6,#1d4ed8)',
                    }}
                  />
                </div>
                <div className="mt-2 flex items-center justify-between text-xs">
                  <span className="text-slate-500">{formatUSD(c.spent)} of {formatUSD(c.budget)}</span>
                  <span className={`font-semibold ${over ? 'text-[var(--gcbc-red-deep)]' : 'text-emerald-700'}`}>
                    {over ? `Over by ${formatUSD(Number(c.spent) - Number(c.budget))}` : `${formatUSD(Number(c.budget) - Number(c.spent))} left`}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex justify-end">
        <button
          onClick={saveAll}
          data-testid="categories-save-btn-bottom"
          className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-[var(--gcbc-royal)] hover:bg-[var(--gcbc-royal-deep)] text-white font-semibold shadow-sm transition-colors"
        >
          <Save className="w-4 h-4" /> Save all changes
        </button>
      </div>
    </div>
  );
}
